// =============================================================================
// USE PAGE DATA HOOK
// =============================================================================
// React hook for loading page data with caching and Firebase integration

import { useState, useEffect } from 'react';
import { dataLoader, type PageDataBundle } from '@/services/dataLoader';

interface UsePageDataOptions {
  /**
   * Whether to preload other pages in the background
   */
  preload?: string[];
  
  /**
   * Callback when data is loaded
   */
  onLoad?: (data: PageDataBundle) => void;
  
  /**
   * Callback when loading fails
   */
  onError?: (error: Error) => void;
}

interface UsePageDataResult {
  data: PageDataBundle | null;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

/**
 * Hook for loading page data with caching and Firebase integration
 * 
 * @example
 * ```tsx
 * function HomePage() {
 *   const { data, loading, error } = usePageData('home', {
 *     preload: ['about', 'services'] // Preload other pages
 *   });
 *   
 *   if (loading) return <LoadingSpinner />;
 *   if (error) return <ErrorPage error={error} />;
 *   if (!data) return null;
 *   
 *   return <ContentRenderer components={data.components} />;
 * }
 * ```
 */
export function usePageData(
  pageId: string,
  options: UsePageDataOptions = {}
): UsePageDataResult {
  const [data, setData] = useState<PageDataBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load the page data
      const pageData = await dataLoader.loadPage(pageId);
      
      if (!pageData) {
        throw new Error(`Failed to load page data for: ${pageId}`);
      }

      setData(pageData);
      
      // Call onLoad callback if provided
      if (options.onLoad) {
        options.onLoad(pageData);
      }

      // Preload other pages in the background if specified
      if (options.preload && options.preload.length > 0) {
        // Don't await - load in background
        dataLoader.preloadPages(options.preload).catch(err => {
          // Preload failed
        });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      
      // Call onError callback if provided
      if (options.onError) {
        options.onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [pageId]); // Reload if pageId changes

  return {
    data,
    loading,
    error,
    reload: loadData,
  };
}

/**
 * Hook specifically for loading navigation data from the home page
 * Other pages can use this to get consistent navigation without loading full page data
 */
export function useNavigationData() {
  const { data, loading, error } = usePageData('home');

  return {
    navItems: data?.navigation?.navItems || [],
    footerLinks: data?.navigation?.footerLinks || [],
    socialLinks: data?.navigation?.socialLinks || [],
    loading,
    error,
  };
}

/**
 * Hook for preloading pages in the background
 * Useful for preloading likely next pages
 */
export function usePreloadPages(pageIds: string[]) {
  useEffect(() => {
    if (pageIds.length === 0) return;

    const preload = async () => {
      try {
        await dataLoader.preloadPages(pageIds);
      } catch (error) {
        // Background preload failed
      }
    };

    // Delay preloading slightly to not interfere with initial page load
    const timeoutId = setTimeout(preload, 1000);

    return () => clearTimeout(timeoutId);
  }, [pageIds.join(',')]);
}

